import { useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import Typography from "components/basic/typography";
import GarageChargerImage from "resources/images/GarageChargerImage.png";
import { useNavigate } from "react-router-dom";
import SignUpFormLayout from "../../components/SignUpFormLayout";
import AutoComplete from "components/basic/autoComplete";
import TextField from "components/basic/textField";
import Checkbox from "components/basic/checkbox";
import { useSnack } from "hooks/useSnack";
import { useFieldErrors } from "hooks/useFieldErrors";
import { useMaskedInput } from "hooks/useMaskedInput";
import { useEffect } from "react";
import { formatPhoneNumber, PHONE_NUMBER_PLACEHOLDER } from "pages/formTools";
import { Account, Vendor } from "../..";
import { validatePrivacyContact } from "./validation";
import { ORGANIZATION_TYPES } from "../../constants";
import Radio from "components/basic/radio";
import {
  notificationEmailChoices,
  selectedNotificationEmail,
} from "../../helpers/notificationEmails";

interface PrivacyContactFormProps {
  signUpData: Vendor;
  setSignUpData: React.Dispatch<React.SetStateAction<Vendor>>;
  account: Account;
}

const PrivacyContactForm: React.FC<PrivacyContactFormProps> = ({
  signUpData,
  setSignUpData,
  account,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const showSnack = useSnack();
  const { t } = useTranslation("auth");
  const { t: tAccount } = useTranslation("account");
  const { errors, validateAll, revalidate } = useFieldErrors(
    validatePrivacyContact,
  );
  const handlePhoneChange = useMaskedInput(formatPhoneNumber);

  // Applies an edit and re-checks any error already on screen.
  const updateField = (patch: Partial<Vendor>) => {
    const next = { ...signUpData, ...patch };
    setSignUpData(next);
    revalidate(next);
  };

  // Pre-fills only empty fields, so edits survive coming back to this step.
  useEffect(() => {
    setSignUpData((prev) => ({
      ...prev,
      primaryContactFirstName:
        prev.primaryContactFirstName || account.firstName,
      primaryContactLastName: prev.primaryContactLastName || account.lastName,
      primaryContactPosition: prev.primaryContactPosition || "Owner",
      primaryContactEmail: prev.primaryContactEmail || account.email,
      primaryContactPhone: prev.primaryContactPhone || account.phoneNumber,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNext = () => {
    if (!validateAll(signUpData)) {
      showSnack(t("common.fix_errors"), "error");
      return;
    }

    navigate("?currentPage=businessRegistrationForm");
  };

  const emailChoices = notificationEmailChoices(account.email, signUpData);
  const notificationEmail = selectedNotificationEmail(
    account.email,
    signUpData,
  );

  const organizationType = signUpData.organizationType
    ? ORGANIZATION_TYPES.find(
        (opt) => opt.label === signUpData.organizationType,
      ) || null
    : null;

  return (
    <SignUpFormLayout
      imageSrc={GarageChargerImage}
      backAction={{
        label: t("common.back"),
        color: "primary",
        onClick: () => navigate("?currentPage=businessInfoForm"),
      }}
      primaryAction={{
        label: t("common.next"),
        color: "warning",
        onClick: handleNext,
      }}
    >
      <div>
        <Typography variant="body2" align="inherit">
          {t("contact.organization_type")}
        </Typography>

        <AutoComplete
          name="organizationType"
          value={organizationType?.id || null}
          options={ORGANIZATION_TYPES.map(({ id, labelKey }) => ({
            id,
            label: tAccount(labelKey),
          }))}
          label={t("common.please_select")}
          onChange={(value) =>
            updateField({
              organizationType:
                ORGANIZATION_TYPES.find(({ id }) => id === value?.id)?.label ??
                "",
            })
          }
          size="tiny"
          textAlign="center"
          error={errors.organizationType ?? undefined}
        />
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: theme.spacing(1),
        }}
      >
        <Typography variant="body2" align="inherit">
          {t("contact.primary_contact")}
        </Typography>

        <TextField
          placeHolder={t("contact.first_name")}
          name="primaryContactFirstName"
          value={signUpData.primaryContactFirstName || ""}
          size="tiny"
          textAlign="right"
          onChange={({ target: { value } }) =>
            updateField({ primaryContactFirstName: value })
          }
          error={errors.primaryContactFirstName ?? undefined}
        />

        <TextField
          placeHolder={t("contact.last_name")}
          name="primaryContactLastName"
          value={signUpData.primaryContactLastName || ""}
          size="tiny"
          textAlign="right"
          onChange={({ target: { value } }) =>
            updateField({ primaryContactLastName: value })
          }
          error={errors.primaryContactLastName ?? undefined}
        />

        <TextField
          placeHolder={t("contact.position")}
          name="primaryContactPosition"
          value={signUpData.primaryContactPosition || ""}
          size="tiny"
          textAlign="right"
          onChange={({ target: { value } }) =>
            updateField({ primaryContactPosition: value })
          }
          error={errors.primaryContactPosition ?? undefined}
        />

        <TextField
          placeHolder={t("contact.email")}
          name="primaryContactEmail"
          value={signUpData.primaryContactEmail || ""}
          size="tiny"
          textAlign="right"
          onChange={({ target: { value } }) =>
            updateField({ primaryContactEmail: value })
          }
          error={errors.primaryContactEmail ?? undefined}
        />

        <TextField
          placeHolder={PHONE_NUMBER_PLACEHOLDER}
          name="primaryContactPhone"
          value={signUpData.primaryContactPhone || ""}
          size="tiny"
          textAlign="right"
          onChange={(event) =>
            updateField({
              primaryContactPhone: handlePhoneChange(
                event,
                signUpData.primaryContactPhone || "",
              ),
            })
          }
          error={errors.primaryContactPhone ?? undefined}
        />
      </div>

      <Checkbox
        isChecked={signUpData.primaryContactIsSignatory}
        onChange={(checked) =>
          updateField({
            primaryContactIsSignatory: checked,
            ...(checked
              ? {
                  signatoryFirstName: null,
                  signatoryLastName: null,
                  signatoryPosition: null,
                  signatoryEmail: null,
                  signatoryPhone: null,
                }
              : {}),
          })
        }
        label={t("contact.can_sign")}
      />

      {!signUpData.primaryContactIsSignatory && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: theme.spacing(1),
          }}
        >
          <Typography variant="body2" align="inherit">
            {t("contact.signatory")}
          </Typography>

          <TextField
            placeHolder={t("contact.first_name")}
            name="signatoryFirstName"
            value={signUpData.signatoryFirstName || ""}
            size="tiny"
            textAlign="right"
            onChange={({ target: { value } }) =>
              updateField({ signatoryFirstName: value })
            }
            error={errors.signatoryFirstName ?? undefined}
          />

          <TextField
            placeHolder={t("contact.last_name")}
            name="signatoryLastName"
            value={signUpData.signatoryLastName || ""}
            size="tiny"
            textAlign="right"
            onChange={({ target: { value } }) =>
              updateField({ signatoryLastName: value })
            }
            error={errors.signatoryLastName ?? undefined}
          />

          <TextField
            placeHolder={t("contact.position")}
            name="signatoryPosition"
            value={signUpData.signatoryPosition || ""}
            size="tiny"
            textAlign="right"
            onChange={({ target: { value } }) =>
              updateField({ signatoryPosition: value })
            }
            error={errors.signatoryPosition ?? undefined}
          />

          <TextField
            placeHolder={t("contact.email")}
            name="signatoryEmail"
            value={signUpData.signatoryEmail || ""}
            size="tiny"
            textAlign="right"
            onChange={({ target: { value } }) =>
              updateField({ signatoryEmail: value })
            }
            error={errors.signatoryEmail ?? undefined}
          />

          <TextField
            placeHolder={PHONE_NUMBER_PLACEHOLDER}
            name="signatoryPhone"
            value={signUpData.signatoryPhone || ""}
            size="tiny"
            textAlign="right"
            onChange={(event) =>
              updateField({
                signatoryPhone: handlePhoneChange(
                  event,
                  signUpData.signatoryPhone || "",
                ),
              })
            }
            error={errors.signatoryPhone ?? undefined}
          />
        </div>
      )}

      {emailChoices.length > 1 && (
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Typography variant="body2" align="inherit">
            {t("contact.preferred_email")}
          </Typography>

          {emailChoices.map((email) => (
            <Radio
              key={email}
              labelPlacement="start"
              isSelected={notificationEmail === email}
              onChange={() => updateField({ notificationEmail: email })}
              label={email}
              value={email}
              textboxBackgroundColor={theme.palette.common.black}
            />
          ))}
        </div>
      )}
    </SignUpFormLayout>
  );
};

export default PrivacyContactForm;
